import { Loader } from '@fukuri-admins/ui/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { Suspense } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useGetPartnerAdminsPartnerFeatureFlags } from '~/hooks';
import { UserDetailHeader } from '~/features/UserDetailHeader';
import { UserDetailProfile } from '~/features/UserDetailProfile';
import { UserDetailProfileBankAccount } from '~/features/UserDetailProfileBankAccount';
import { getUsersLink } from '~/utils/getLinks';
import styles from './styles.module.scss';

type UserDetailContentProps = {
  /** 利用者ID */
  userId: number;
};

/**
 * 利用者詳細コンテンツ
 *
 * partner_feature_flag.bank_account の ON/OFF で表示する Feature を切り替える。
 * `useGetPartnerAdminsPartnerFeatureFlags` は suspense: true のため、
 * 親 Screen の Suspense 境界内でレンダーされる必要がある。
 */
const UserDetailContent: React.FC<UserDetailContentProps> = ({ userId }) => {
  const threeLetterCode = getThreeLetterCode() ?? '';
  const { data } = useGetPartnerAdminsPartnerFeatureFlags({ threeLetterCode });

  const isBankAccountEnabled = (data.partner_feature_flags ?? []).some(
    f => f.feature_key === 'bank_account' && f.enabled
  );

  return isBankAccountEnabled ? (
    <UserDetailProfileBankAccount userId={userId} />
  ) : (
    <UserDetailProfile userId={userId} />
  );
};

/**
 * 利用者詳細 Screen（新パターン）
 *
 * - URL パラメータ `:user_id` を取得して利用者 ID に変換する
 * - UserDetailHeader（パンくずリスト）と UserDetailProfile（詳細情報）を Suspense でラップする
 * - partner_feature_flag.bank_account が ON の場合は UserDetailProfileBankAccount を表示し、
 *   OFF の場合は既存の UserDetailProfile を表示する
 */
export const Screen: React.FC = () => {
  const { user_id = '' } = useParams<{ user_id: string }>();
  const userId = Number.parseInt(user_id, 10);

  if (Number.isNaN(userId)) {
    return <Navigate to={getUsersLink()} replace />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <UserDetailHeader userId={userId} />
      <main className={styles.body}>
        <UserDetailContent userId={userId} />
      </main>
    </Suspense>
  );
};
